import toast, { type Renderable } from 'react-hot-toast';

interface NotificationConfig {
  duration?: number;
  icon?: Renderable | null;
  id?: string;
  position?: 'top-center' | 'top-right' | 'top-left' | 'bottom-center' | 'bottom-right' | 'bottom-left';
}

export function sanitizeUserFacingErrorMessage(raw: any): string {
  const message = String(raw || '').trim();
  if (!message) return 'Something went wrong. Please try again.';

  const lower = message.toLowerCase();

  const looksLikeSupplierOrProviderDetail =
    lower.includes('supplier') ||
    lower.includes('provider') ||
    lower.includes('hubnet') ||
    lower.includes('ghinternet') ||
    lower.includes('failed to check') ||
    lower.includes('endpoint') ||
    lower.includes('httpstatus') ||
    lower.includes('admin manual verification required') ||
    lower.includes('manual verification required') ||
    lower.includes('auto-processing') ||
    lower.includes('auto processing') ||
    lower.includes('automatic processing');

  const looksLikeAutoProcessingFailure =
    lower.includes('order processing failed') ||
    lower.includes('cannot be processed') ||
    lower.includes('queued for manual processing') ||
    lower.includes('requires manual');

  const isUserWalletInsufficientBalance =
    (lower.includes('insufficient') && lower.includes('balance')) &&
    !lower.includes('supplier');

  if (isUserWalletInsufficientBalance) {
    return message;
  }

  if (looksLikeSupplierOrProviderDetail || looksLikeAutoProcessingFailure) {
    return 'Order could not be processed automatically. Please try again later or contact support.';
  }

  return message;
}

/**
 * Sanitize displayed failure reasons in UI components (order tables, tracking, banners).
 * Unlike sanitizeUserFacingErrorMessage (for toast notifications), this is for persistent data.
 * Admins see the raw reason; non-admin users see a customer-friendly message.
 */
export function sanitizeDisplayedFailureReason(reason: string | null | undefined, isAdmin: boolean = false): string | null {
  if (!reason) return null;
  if (isAdmin) return reason;

  const lower = reason.toLowerCase();
  const isSupplierInternal =
    lower.includes('supplier issue') ||
    lower.includes('manual processing') ||
    lower.includes('supplier balance') ||
    lower.includes('hubnet') ||
    lower.includes('ghinternet') ||
    lower.includes('spfastit') ||
    lower.includes('mobigh') ||
    lower.includes('telecel supplier') ||
    lower.includes('provider routing failed') ||
    lower.includes('auto-delivery failed');

  return isSupplierInternal ? 'Data is being processed' : reason;
}

class NotificationManager {
  private static instance: NotificationManager;
  private notificationHistory: Map<string, number> = new Map();
  private readonly DEBOUNCE_TIME = 3000; // 3 seconds
  private readonly MAX_DUPLICATES = 2; // Max same notification in time window

  private constructor() {}

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  /**
   * Check if notification should be shown (anti-spam)
   */
  private shouldShowNotification(message: string): boolean {
    const key = message.toLowerCase().trim();
    const now = Date.now();
    const lastShown = this.notificationHistory.get(key);
    
    if (lastShown && now - lastShown < this.DEBOUNCE_TIME) {
      return false; // Skip duplicate notification
    }
    
    this.notificationHistory.set(key, now);
    
    // Clean old entries
    this.cleanupOldEntries();
    
    return true;
  }

  /**
   * Clean up old notification history entries
   */
  private cleanupOldEntries(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.notificationHistory.forEach((timestamp, key) => {
      if (now - timestamp > this.DEBOUNCE_TIME * 3) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.notificationHistory.delete(key);
    });
  }

  /**
   * Show success notification
   */
  success(message: string, config: NotificationConfig = {}): void {
    if (!this.shouldShowNotification(message)) return;
    
    toast.success(message, {
      duration: config.duration || 4000,
      icon: config.icon === undefined ? '✅' : config.icon,
      id: config.id,
      position: config.position || 'top-center'
    });
  }

  /**
   * Show error notification
   */
  error(message: string, config: NotificationConfig = {}): void {
    if (!this.shouldShowNotification(message)) return;
    
    toast.error(message, {
      duration: config.duration || 5000,
      icon: config.icon === undefined ? '❌' : config.icon,
      id: config.id,
      position: config.position || 'top-center'
    });
  }

  /**
   * Show loading notification
   */
  loading(message: string, config: NotificationConfig = {}): string {
    return toast.loading(message, {
      duration: config.duration || 30000, // 30 seconds max for loading
      icon: config.icon === undefined ? '⏳' : config.icon,
      id: config.id,
      position: config.position || 'top-center'
    });
  }

  /**
   * Show info notification
   */
  info(message: string, config: NotificationConfig = {}): void {
    if (!this.shouldShowNotification(message)) return;
    
    toast(message, {
      duration: config.duration || 4000,
      icon: config.icon === undefined ? 'ℹ️' : config.icon,
      id: config.id,
      position: config.position || 'top-center'
    });
  }

  /**
   * Show warning notification
   */
  warning(message: string, config: NotificationConfig = {}): void {
    if (!this.shouldShowNotification(message)) return;
    
    toast(message, {
      duration: config.duration || 4500,
      icon: config.icon === undefined ? '⚠️' : config.icon,
      id: config.id,
      position: config.position || 'top-center'
    });
  }

  /**
   * Dismiss notification by ID
   */
  dismiss(id?: string): void {
    toast.dismiss(id);
  }

  /**
   * Handle API errors with smart messaging
   */
  handleApiError(error: any, context: string = ''): void {

    let message = 'An unexpected error occurred';
    
    if (error?.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          message = data?.error || data?.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          message = 'Please login again to continue.';
          break;
        case 403:
          message = 'You do not have permission to perform this action.';
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 503:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          message = data?.error || data?.message || `Request failed (${status})`;
      }
    } else if (error?.message) {
      if (error.message.includes('Network Error')) {
        message = 'Network connection error. Please check your internet connection.';
      } else if (error.message.includes('timeout')) {
        message = 'Request timed out. Please try again.';
      } else {
        message = error.message;
      }
    }
    
    message = sanitizeUserFacingErrorMessage(message);

    // Only show error if it's not a common background error
    if (!this.isBackgroundError(context)) {
      this.error(message, { id: `error-${context}` });
    }
  }

  /**
   * Check if error is from background operation that shouldn't show toast
   */
  private isBackgroundError(context: string): boolean {
    const backgroundContexts = [
      'wallet-balance-sync',
      'sales-ladder-poll',
      'notification-check',
      'auto-refresh'
    ];
    
    return backgroundContexts.some(bg => context.toLowerCase().includes(bg));
  }

  /**
   * Show payment success with wallet update
   */
  paymentSuccess(amount: number, newBalance: number): void {
    this.success(`Payment successful! Added GHS ${amount.toFixed(2)} to your wallet.`, {
      duration: 5000,
      icon: '💰'
    });
  }

  /**
   * Show payment failure with helpful message
   */
  paymentFailure(reason: string = 'Payment failed'): void {
    this.error(`${reason}. Please try again or contact support if the issue persists.`, {
      duration: 6000,
      icon: '💳'
    });
  }

  /**
   * Show data operation success
   */
  dataSuccess(operation: string, count?: number): void {
    const message = count 
      ? `${operation} completed successfully (${count} items)`
      : `${operation} completed successfully`;
    
    this.success(message, { duration: 3000 });
  }

  /**
   * Show data loading error (less intrusive)
   */
  dataError(operation: string): void {
    this.warning(`Failed to load ${operation}. Some data may be unavailable.`, {
      duration: 4000,
      icon: '📊'
    });
  }

  /**
   * Show validation error
   */
  validationError(field: string, message: string): void {
    this.error(`${field}: ${message}`, {
      duration: 4000,
      icon: '⚠️'
    });
  }

  /**
   * Show network status change
   */
  networkStatus(isOnline: boolean): void {
    if (isOnline) {
      this.success('Connection restored', {
        duration: 3000,
        icon: '🌐'
      });
    } else {
      this.warning('Connection lost. Some features may be unavailable.', {
        duration: 5000,
        icon: '📡'
      });
    }
  }

  /**
   * Clear all notifications
   */
  clear(): void {
    toast.dismiss();
  }
}

// Export singleton instance
export const notificationManager = NotificationManager.getInstance();

// Export convenience functions that use the manager
export const showSuccess = (message: string, config?: NotificationConfig) => 
  notificationManager.success(message, config);

export const showError = (message: string, config?: NotificationConfig) => 
  notificationManager.error(message, config);

export const showLoading = (message: string, config?: NotificationConfig) => 
  notificationManager.loading(message, config);

export const showInfo = (message: string, config?: NotificationConfig) => 
  notificationManager.info(message, config);

export const showWarning = (message: string, config?: NotificationConfig) => 
  notificationManager.warning(message, config);

export const dismissNotification = (id?: string) => 
  notificationManager.dismiss(id);

export const handleApiError = (error: any, context?: string) => 
  notificationManager.handleApiError(error, context);

export const clearNotifications = () => 
  notificationManager.clear();
